import React, { useState, useEffect } from 'react';
import { Marker } from 'google-maps-react';
import { Modal } from 'react-bootstrap';
import { MdDirectionsBus, MdTrain, MdRefresh } from 'react-icons/md';
import { FaWalking } from 'react-icons/fa';
import { VscLoading } from 'react-icons/vsc';
import { HiOutlineBuildingOffice2 } from 'react-icons/hi2';

import {
    StopHeader,
    StopMeta,
    StopType,
    TripPlanningCard,
    PredictionsList,
    PredictionItem,
    LoadingState,
    ErrorState,
    NoArrivalsState,
} from '../styles/TransitOverlayStyles';
import { fetchArrivalsForStop, fetchNearbyTransitStops } from '@/_services/integrations/map/transit.service';
import { css } from '@emotion/react';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';

const Component = ({ google, map, mapRef, center }) => {
    const [loading, setLoading] = useState(false);
    const [selectedStop, setSelectedStop] = useState(null);
    const [predictions, setPredictions] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [transitStops, setTransitStops] = useState([]);

    useEffect(() => {
        console.log({
            google,
            mapRef,
        });
        if (!google || !mapRef.current) return;

        const transitLayer = new google.maps.TransitLayer();
        transitLayer.setMap(mapRef.current.map);

        loadNearbyStops();

        return () => transitLayer.setMap(null);
    }, [google, mapRef.current, center]);

    const loadNearbyStops = async () => {
        setLoading(true);
        const stops = await fetchNearbyTransitStops(center.lat, center.lng);
        console.log(stops);
        setTransitStops(stops);
        setLoading(false);
    };

    const handleStopClick = async (stop) => {
        setSelectedStop(stop);
        setRefreshing(true);
        const arrivals = await fetchArrivalsForStop(stop);
        setPredictions(arrivals);
        setRefreshing(false);
    };

    const handleRefresh = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (selectedStop) handleStopClick(selectedStop);
    };

    return (
        <>
            {transitStops.map((stop) => (
                <Marker
                    key={stop.id}
                    position={{ lat: stop.lat, lng: stop.lng }}
                    title={stop.name}
                    map={map}
                    google={google}
                    icon={{
                        url:
                            stop.type === 'bus'
                                ? 'https://cdn-icons-png.flaticon.com/128/10981/10981034.png'
                                : 'https://cdn-icons-png.flaticon.com/128/988/988554.png',
                        scaledSize: new google.maps.Size(25, 25),
                    }}
                    onClick={() => handleStopClick(stop)}
                />
            ))}

            {selectedStop && (
                <Modal show={selectedStop} onHide={() => setSelectedStop(null)} centered>
                    <Modal.Header closeButton>
                        <StopHeader>
                            <h3>{selectedStop?.name}</h3>
                        </StopHeader>
                    </Modal.Header>
                    <Modal.Body>
                        <StopMeta>
                            <StopType provider={selectedStop.provider}>
                                {selectedStop.type === 'bus' ? (
                                    <MdDirectionsBus size={20} className="transport-icon" />
                                ) : (
                                    <MdTrain size={20} className="transport-icon" />
                                )}
                                <span style={{ fontWeight: 500 }}>
                                    {selectedStop.type === 'bus' ? 'Bus Stop' : 'Train Station'}
                                </span>
                                <span className="provider-badge">{selectedStop.provider}</span>
                                <span className="distance">
                                    <FaWalking size={14} />
                                    {Math.round(selectedStop.distance)} m
                                </span>
                            </StopType>

                            <TripPlanningCard>
                                <div className="card-title">Trip Planning</div>
                                <div className="planning-details">
                                    <div className="detail-item">
                                        <span>Walking time</span>
                                        <span className="detail-value">
                                            {Math.ceil(selectedStop.distance / 80)} min
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <span>Walking distance</span>
                                        <span className="detail-value">{Math.ceil(selectedStop.distance)} m</span>
                                    </div>
                                    <div className="detail-item">
                                        <span>Next arrival</span>
                                        <span className="detail-value">{predictions[0]?.minutes || 0} min</span>
                                    </div>
                                    <div className="detail-item">
                                        <span>Live tracking</span>
                                        <span className="detail-value">
                                            {predictions.filter((p) => p.hasRealTime).length} vehicles
                                        </span>
                                    </div>
                                </div>
                            </TripPlanningCard>
                        </StopMeta>

                        <div style={{ padding: '10px 16px 8px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                    zIndex: '999 !important',
                                }}
                            >
                                <h4 style={{ margin: 0, fontSize: '14px', color: '#333' }}>
                                    <u>Upcoming Arrivals</u>
                                </h4>

                                <button
                                    onClick={handleRefresh}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#667eea',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <MdRefresh size={14} />
                                    Refresh
                                </button>
                            </div>

                            {error ? (
                                <ErrorState>
                                    <p>Unable to load arrivals</p>
                                    <button onClick={handleRefresh}>
                                        <MdRefresh /> Try Again
                                    </button>
                                </ErrorState>
                            ) : refreshing ? (
                                <LoadingState>
                                    <VscLoading className="spin" />
                                    <p>Loading arrivals...</p>
                                </LoadingState>
                            ) : predictions.length === 0 ? (
                                <NoArrivalsState>
                                    <p>No upcoming arrivals</p>
                                    <span style={{ fontSize: '12px', color: '#666' }}>
                                        Check schedule for later times
                                    </span>
                                </NoArrivalsState>
                            ) : (
                                <PredictionsList>
                                    {predictions.map((prediction, index) => (
                                        <PredictionItem
                                            key={`${prediction.routeId}-${index}`}
                                            arriving={prediction.minutes <= 1}
                                            soon={prediction.minutes > 1 && prediction.minutes <= 5}
                                            isCanceled={prediction.isCanceled}
                                        >
                                            <div className="route-info">
                                                <div className="route-header">
                                                    <span className="route-id">Route ID: {prediction.routeId}</span>
                                                    {prediction.isCanceled ? (
                                                        <span className="status-indicator canceled">Canceled</span>
                                                    ) : prediction.hasRealTime ? (
                                                        <span className="status-indicator live">Live</span>
                                                    ) : (
                                                        <span className="status-indicator scheduled">Scheduled</span>
                                                    )}
                                                </div>

                                                <div className="agency">
                                                    <HiOutlineBuildingOffice2 className="mr-2" />
                                                    {prediction.agencyName}
                                                </div>
                                                <div className="destination">
                                                    Trip Head Sign: {prediction.tripHeadSign}
                                                </div>
                                                <div className="destination" title={prediction.routeLongName}>
                                                    Route: {prediction.routeLongName}
                                                </div>
                                            </div>

                                            <div className="arrival-info">
                                                <div className="minutes">
                                                    {prediction.isCanceled ? 'Canceled' : `${prediction.minutes} min`}
                                                </div>
                                                {!prediction.isCanceled && (
                                                    <div className="arrival-time">{prediction.arrivalTime}</div>
                                                )}
                                            </div>
                                        </PredictionItem>
                                    ))}
                                </PredictionsList>
                            )}
                        </div>
                    </Modal.Body>
                </Modal>
            )}

            {loading && !transitStops.length && (
                <div
                    css={css`
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        background-color: white;
                        padding: 16px;
                        border-radius: 8px;
                        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        z-index: 10;
                    `}
                >
                    <VscLoading
                        className="spin"
                        css={css`
                            font-size: 24px;
                            margin-bottom: 8px;
                        `}
                    />
                    <p
                        css={css`
                            margin: 0;
                            color: #666;
                        `}
                    >
                        Finding nearby transit stops...
                    </p>
                </div>
            )}
        </>
    );
};

const TransitOverlay = (props) => (
    <ComponentErrorBoundary componentErrorMessage={'Failed to load transit overlay'}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default TransitOverlay;
